/**
 * ThreeYearModel — the editable three-year funnel model that IS the 2027 Plan
 * tab (quick-260928-lh8, John 2026-09-28).
 *
 * Reads top-down, cause before effect:
 *   presets
 *   1 · NEW-LOGO INPUTS   — feed only the new-logo funnel
 *   2 · EXPANSION INPUTS  — feed only the expansion line and the retained base
 *   3 · RESULTS           — total ARR forecast (new logo vs expansion), the
 *                           new-logo funnel by year, and the pipeline the
 *                           target needs vs what the funnel produces.
 *
 * Inputs are pre-populated with today's actuals: live dashboard aggregates
 * where one exists, the Funnel Metric Definitions workbook (dated constants)
 * for the rest. All math lives in @saledash/shared/plan2027/model3y (pure,
 * tested). Nothing is persisted — edits live in the browser until reload.
 */
// Value imports come from the pure subpath, not the barrel: the barrel pulls
// node:fs / node:crypto into the browser bundle (same pattern as forecast/*).
import { FUNNEL_TRENDS } from '@saledash/shared/plan2027/funnel-trends';
import {
  buildActuals,
  buildActualsMeta,
  FUNNEL_BASELINE_2026_09,
  leverOptions,
  MODEL3Y_BENCHMARKS,
  MODEL3Y_DEFAULT_PRESET,
  MODEL3Y_PRESETS,
  PLAN_ARR_GROWTH,
  PLAN2027_NEW_LOGO_TARGET,
  pipelineInHand,
  PRIOR_Q4_ROLL_FORWARD_POSITIONS,
  priorQ4RollForwardBase,
  requiredChain,
  runModel,
  targetWalk,
} from '@saledash/shared/plan2027/model3y';
import { TREND_FOR_INPUT } from '@saledash/shared/plan2027/trend-periods';
import ReactECharts from 'echarts-for-react';
import { Fragment, useMemo, useState } from 'react';

import type { Plan2027Baseline, Plan2027CycleTiming } from '@saledash/shared';
import type { InputBenchmark, Model3yInputs, Model3yLiveBaseline, Model3yPresetId, PresetContext, WalkRow } from '@saledash/shared/plan2027/model3y';

// ---- formatters ------------------------------------------------------------

function fmtM(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (a >= 1_000_000) return `${sign}$${(Math.round(a / 100_000) / 10).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${String(Math.round(a / 1_000))}K`;
  return `${sign}$${String(Math.round(a))}`;
}
/** Exact dollars with commas, for arithmetic shown to the reader. */
function fmtD(v: number): string {
  return `$${Math.round(v).toLocaleString('en-US')}`;
}
function pct1(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
function num(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return v >= 100 ? Math.round(v).toLocaleString('en-US') : v.toFixed(1).replace(/\.0$/, '');
}
function fmtWalk(v: number, unit: WalkRow['unit']): string {
  if (unit === 'money') return fmtM(v);
  if (unit === 'pct') return pct1(v);
  if (unit === 'ratio') return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
  return num(v);
}
function fyLabel(planYear: number, i: number): string {
  return `FY${String(planYear + i)}`;
}
/** "12% above" / "8% below" / "equal to" the historical expansion. */
function expansionVsHistorical(v: number, hist: number): string {
  if (hist <= 0) return 'vs';
  const d = v / hist - 1;
  if (Math.abs(d) < 0.005) return 'equal to';
  return `${(Math.abs(d) * 100).toFixed(0)}% ${d > 0 ? 'above' : 'below'}`;
}

// ---- input field -----------------------------------------------------------

type FieldKind = 'pct' | 'num' | 'int' | 'money';

interface FieldProps {
  id: keyof Model3yInputs;
  label: string;
  kind: FieldKind;
  value: number;
  actual: number;
  source: string;
  step?: number;
  onChange: (id: keyof Model3yInputs, v: number) => void;
}

/** Display value in the field's unit with thousands separators (pct fields edit as 0..100). */
function toDisplay(kind: FieldKind, v: number): string {
  if (kind === 'pct') return (v * 100).toFixed(1).replace(/\.0$/, '');
  if (kind === 'money' || kind === 'int') return Math.round(v).toLocaleString('en-US');
  return (Math.round(v * 10) / 10).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}
/** Parse what the user typed: commas, spaces, $ and % are ignored; a lone "." or "-" mid-edit is not a value yet. */
function fromDisplay(kind: FieldKind, raw: string): number | null {
  const cleaned = raw.replace(/[,\s$%]/g, '');
  if (cleaned === '' || cleaned === '.' || cleaned === '-') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return kind === 'pct' ? n / 100 : n;
}
function fmtActual(kind: FieldKind, v: number): string {
  if (kind === 'pct') return pct1(v);
  if (kind === 'money') return fmtM(v);
  return num(v);
}

/**
 * Benchmark pill beside an input (quick-261007-ffh): the typical band in the input's
 * unit, a dot coloured by where the current value sits (green = at or better than the
 * band, amber = short of it), the source on hover. One short pill, nothing else.
 */
function BenchPill({ bench, value, kind }: { bench: InputBenchmark; value: number; kind: FieldKind }): React.JSX.Element {
  const better = bench.better === 'high' ? value >= bench.low : value <= bench.high;
  const within = value >= bench.low && value <= bench.high;
  const dot = better ? 'bg-good' : 'bg-warn';
  const where = within ? 'within the benchmark band' : better ? 'better than the benchmark band' : 'short of the benchmark band';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-ink-100 bg-ink-50 px-1.5 py-px text-[10px] font-medium text-ink-700"
      title={`Benchmark ${bench.text} — ${bench.source}. Current ${fmtActual(kind, value)} is ${where}.`}
      data-testid="bench"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
      <span className="text-ink-500">Bench</span> {bench.text}
    </span>
  );
}

function Field({ id, label, kind, value, actual, source, step, onChange }: FieldProps): React.JSX.Element {
  const changed = Math.abs(value - actual) > (kind === 'pct' ? 0.0005 : 0.05);
  const unit = kind === 'pct' ? '%' : kind === 'money' ? '$' : '';
  const bench = MODEL3Y_BENCHMARKS[id];
  // the measured history behind this box, in section 5 (quick-261013-hqb)
  const trend = TREND_FOR_INPUT[id];
  return (
    <label className="flex flex-col gap-1 rounded-[10px] border border-ink-100 bg-white p-3">
      <span className="text-[10.5px] font-bold uppercase tracking-wider text-ink-500">{label}</span>
      <span className="flex items-center gap-1.5">
        {kind === 'money' && <span className="text-xs text-ink-500">{unit}</span>}
        <input
          type="text"
          inputMode="decimal"
          data-step={step ?? (kind === 'pct' ? 0.5 : kind === 'money' ? 1000 : kind === 'int' ? 1 : 0.1)}
          value={toDisplay(kind, value)}
          onChange={(e) => {
            const v = fromDisplay(kind, e.target.value);
            if (v != null) onChange(id, Math.max(0, v));
          }}
          aria-label={label}
          className={`w-full rounded-md border px-2 py-1.5 text-sm font-semibold tabular-nums text-ink-900 outline-none focus:ring-2 focus:ring-adeptia-cyan ${
            changed ? 'border-adeptia-cyan bg-adeptia-cyan/10' : 'border-ink-200 bg-ink-50'
          }`}
        />
        {kind === 'pct' && <span className="text-xs text-ink-500">{unit}</span>}
      </span>
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10.5px] leading-tight text-ink-500">
        <span>
          Actual <b className="font-semibold text-ink-700">{fmtActual(kind, actual)}</b>
        </span>
        {bench && <BenchPill bench={bench} value={value} kind={kind} />}
        {trend && (
          <a
            href={`#trend-${trend}`}
            className="inline-flex items-center rounded-full border border-ink-100 bg-ink-50 px-1.5 py-px text-[10px] font-medium text-ink-700 hover:border-adeptia-cyan hover:text-ink-900"
            title="Jump to this input's year-to-date trend in section 5"
            data-testid="trend-link"
          >
            trend &darr;
          </a>
        )}
      </span>
      <span className="text-[10px] leading-tight text-ink-500">{source}</span>
    </label>
  );
}

/** Input group with, underneath, the live chain those boxes produce (first plan year). */
function Group({ title, chain, children }: { title: string; chain?: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-500">{title}</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
      {chain != null && (
        <div className="mt-2 rounded-md bg-ink-50 px-3 py-1.5 font-mono text-[11px] leading-snug text-ink-700" data-testid="chain">
          {chain}
        </div>
      )}
    </div>
  );
}

/** Numbered section frame: what it is, what it feeds. */
function Section({
  n,
  title,
  feeds,
  tone,
  children,
}: {
  n: string;
  title: string;
  feeds: string;
  tone: 'cyan' | 'green' | 'ink';
  children: React.ReactNode;
}): React.JSX.Element {
  const edge = tone === 'cyan' ? 'border-l-adeptia-cyan' : tone === 'green' ? 'border-l-good' : 'border-l-ink-900';
  return (
    <section className={`rounded-[14px] border border-ink-100 border-l-4 bg-white p-5 shadow-[0_1px_2px_rgba(16,28,45,0.05)] ${edge}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500">{n}</span>
        <h2 className="text-base font-bold tracking-tight text-ink-900">{title}</h2>
      </div>
      <p className="mt-1 max-w-[94ch] text-xs text-ink-700">{feeds}</p>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function LeverList({ title, items }: { title: string; items: { label: string; doable: boolean; why: string }[] }): React.JSX.Element {
  return (
    <div>
      <div className="text-xs font-semibold text-ink-900">{title}</div>
      <ol className="mt-1 space-y-1">
        {items.map((x, i) => (
          <li key={x.label} className="flex items-baseline gap-2 text-xs text-ink-700">
            <span className="w-5 shrink-0 text-right tabular-nums text-ink-500">{String(i + 1)}.</span>
            <span className="flex-1">
              {x.label} &mdash;{' '}
              <b className={`font-bold ${x.doable ? 'text-good-text' : 'text-bad-text'}`}>{x.doable ? 'doable' : 'not doable'}</b>
              <span className="text-ink-500"> ({x.why})</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Out({ label, value, note, tone }: { label: string; value: string; note: string; tone?: 'good' | 'warn' | 'bad' }): React.JSX.Element {
  const cls = tone === 'good' ? 'text-good-text' : tone === 'warn' ? 'text-warn-text' : tone === 'bad' ? 'text-bad-text' : 'text-ink-900';
  return (
    <div className="bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`mt-1 text-xl font-bold leading-none tracking-tight tabular-nums ${cls}`}>{value}</div>
      <div className="mt-1 text-[10.5px] leading-tight text-ink-500">{note}</div>
    </div>
  );
}

// ---- component ---------------------------------------------------------------

export interface ThreeYearModelProps {
  baseline: Plan2027Baseline;
  cycleTiming: Plan2027CycleTiming;
  /** Default target: new-logo ARR per year ($). */
  defaultTargetNewArr: number;
  planYear: number;
}

export function ThreeYearModel({ baseline, cycleTiming, defaultTargetNewArr, planYear }: ThreeYearModelProps): React.JSX.Element {
  const live: Model3yLiveBaseline = useMemo(
    () => ({
      winRate: baseline.winRate,
      avgWonValue: baseline.avgWonValue,
      avgCreatedOppValue: baseline.avgCreatedOppValue,
      inboundLeadsPerQuarter: baseline.inboundLeadsPerQuarter,
      oppsCreatedPerQuarter: baseline.oppsCreatedPerQuarter,
      oppToWinDays: cycleTiming.oppToWinDays,
      targetNewArrPerYear: defaultTargetNewArr,
      // pipeline in hand (quick-261008-mi0): open New Business close-dated in the plan
      // year, and the prior Q4's Best Case + Pipeline (75% of it rolls forward)
      openPlanYearPipeline: baseline.openPipeline.planYear.amount,
      openPriorQ4RollForwardPipeline: priorQ4RollForwardBase(baseline.openPipeline.priorQ4ByPosition).amount,
    }),
    [baseline, cycleTiming, defaultTargetNewArr],
  );
  const actuals = useMemo(() => buildActuals(live), [live]);
  const meta = useMemo(() => buildActualsMeta(live), [live]);
  const actualsRun = useMemo(() => runModel(actuals), [actuals]);
  const carryIn = actualsRun.years[0]?.production ?? 0;
  // What presets may read besides the actuals: the carry-in, the Metric Trends series
  // (Likely) and the trace scale (quick-261007-ffh).
  const ctx: PresetContext = useMemo(() => ({ carryIn, trends: FUNNEL_TRENDS, mtgToOppScale: meta.mtgToOppScale }), [carryIn, meta.mtgToOppScale]);

  // Opens on the plan-growth preset (John 2026-09-28: years must not flatline); the
  // rates are today's actuals plus the plan shape, win rate solved to 22% ARR growth.
  const [inputs, setInputs] = useState<Model3yInputs>(() => {
    const p = MODEL3Y_PRESETS.find((x) => x.id === MODEL3Y_DEFAULT_PRESET);
    return p ? p.apply(actuals, ctx) : actuals;
  });
  const [preset, setPreset] = useState<Model3yPresetId | 'custom'>(MODEL3Y_DEFAULT_PRESET);

  const result = useMemo(() => runModel(inputs, carryIn), [inputs, carryIn]);
  const req = useMemo(() => requiredChain(inputs, result), [inputs, result]);
  // quick-261030-ka4: run rate (actuals, constant) vs needed at today's rates vs the scenario
  const walk = useMemo(() => targetWalk(actuals, inputs, carryIn), [actuals, inputs, carryIn]);
  const levers = useMemo(() => leverOptions(inputs, result, carryIn), [inputs, result, carryIn]);

  const setField = (id: keyof Model3yInputs, v: number): void => {
    setInputs((prev) => ({ ...prev, [id]: v }));
    setPreset('custom');
  };
  const applyPreset = (id: Model3yPresetId): void => {
    const p = MODEL3Y_PRESETS.find((x) => x.id === id);
    if (!p) return;
    // presets are relative to actuals; keep the user's target
    setInputs({ ...p.apply(actuals, ctx), targetNewArrPerYear: inputs.targetNewArrPerYear });
    setPreset(id);
  };
  const presetDescription = (p: { description: string; describe?: (a: Model3yInputs, c?: PresetContext) => string }): string =>
    p.describe ? `${p.description} ${p.describe(actuals, ctx)}` : p.description;

  const yearLabel = (i: number): string => fyLabel(planYear, i);
  const chartOption = useMemo(() => {
    const cats = [`Open ${fyLabel(planYear, 0)}`, ...result.years.map((_, i) => fyLabel(planYear, i))];
    const retained = [inputs.startingArr, ...result.years.map((y) => y.openingArr - y.churn)];
    const expansion = [0, ...result.years.map((y) => y.expansion)];
    const newArr = [0, ...result.years.map((y) => y.newArr)];
    return {
      grid: { left: 64, right: 16, top: 16, bottom: 56 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => (typeof v === 'number' ? fmtM(v) : ''),
      },
      legend: { bottom: 0, itemWidth: 12, itemHeight: 12, textStyle: { fontSize: 11, color: '#64748b' } },
      xAxis: { type: 'category', data: cats, axisLabel: { color: '#64748b', fontSize: 11 } },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#64748b', fontSize: 10, formatter: (v: number) => fmtM(v) },
        splitLine: { lineStyle: { color: '#f1f5f9' } },
      },
      series: [
        { name: 'Retained base', type: 'bar', stack: 'arr', data: retained, itemStyle: { color: '#111d36' }, barMaxWidth: 72 },
        { name: 'Expansion (current customers)', type: 'bar', stack: 'arr', data: expansion, itemStyle: { color: '#16a34a' } },
        {
          name: 'New logo',
          type: 'bar',
          stack: 'arr',
          data: newArr,
          itemStyle: { color: '#3ccbf8' },
          label: {
            show: true,
            position: 'top',
            fontSize: 11,
            fontWeight: 600,
            color: '#0f172a',
            formatter: (p: { dataIndex: number }) => {
              const i = p.dataIndex;
              const total = i === 0 ? inputs.startingArr : (result.years[i - 1]?.endingArr ?? 0);
              return fmtM(total);
            },
          },
        },
      ],
    };
  }, [result, inputs.startingArr, planYear]);

  const steady = result.steady;
  const y1 = result.years[0];
  const verdictTone = req.verdict === 'on-target' ? 'good' : req.verdict === 'gap-small' ? 'warn' : 'bad';
  const verdictCls =
    verdictTone === 'good'
      ? 'border-good/50 bg-good-bg text-good-text'
      : verdictTone === 'warn'
        ? 'border-warn/50 bg-warn-bg text-warn-text'
        : 'border-bad/50 bg-bad-bg text-bad-text';

  const fb = FUNNEL_BASELINE_2026_09;
  const wb = `workbook, ${fb.funnelWindow}`;
  const wbDeal = `workbook, ${fb.dealWindow}`;
  const traceNote = `workbook trace × ${meta.mtgToOppScale.toFixed(2)} so opps match Salesforce New Business opps created (${num(meta.nbOppsPerYear)}/yr vs ${num(meta.tracedOppsPerYear)} traced to a tagged meeting) — every new-logo opp is meeting-sourced`;
  // live chain for the first plan year, one line per input group
  const c0 = result.years[0];
  const inWins = c0 ? c0.inboundOpps * c0.winRate : 0;
  const obWins = c0 ? c0.outboundOpps * c0.winRate : 0;
  // quick-261029-fkq: BDRs and AEs dial at their own rates; the AEs then work the delivered meetings
  const chainCapacity = c0
    ? `${num(c0.effectiveBdrs)} BDRs × ${num(inputs.conversationsPerBdrMonth)}/mo × 12 = ${num(c0.bdrConversations)} + ${num(c0.effectiveSellers)} AEs × ${num(inputs.conversationsPerSellerMonth)}/mo × 12 = ${num(c0.sellerConversations)} → ${num(c0.conversations)} conversations/yr · ${num(inputs.inboundLeadsPerMonth)} leads/mo × 12 = ${num(c0.inboundLeads)} inbound leads/yr`
    : '';
  const chainAeCapacity = c0
    ? `${num(c0.effectiveSellers)} AEs × ${num(inputs.meetingsPerAeMonth)} meetings/mo × 12 = ${num(c0.aeMeetingCapacity)} meetings they can work · ${num(c0.inboundDelivered + c0.outboundDelivered + c0.meetingsUnworked)} delivered = ${pct1(c0.aeLoad)} loaded${c0.meetingsUnworked > 0 ? ` → ${num(c0.meetingsUnworked)} go unworked` : ''}`
    : '';
  const chainInbound = c0
    ? `${num(c0.inboundLeads)} leads × ${pct1(inputs.leadToBooked)} = ${num(c0.inboundBooked)} booked → × ${pct1(inputs.inboundDeliveredOfBooked)} = ${num(c0.inboundDelivered)} delivered → × ${pct1(c0.inboundOpps / Math.max(1e-9, c0.inboundDelivered))} = ${num(c0.inboundOpps)} opps → × ${pct1(c0.winRate)} = ${num(inWins)} won → ${fmtM(inWins * c0.avgWonValue)}`
    : '';
  const chainOutbound = c0
    ? `${num(c0.conversations)} conversations ÷ ${num(inputs.conversationsPerMeeting)} = ${num(c0.outboundBooked)} booked → × ${pct1(inputs.outboundDeliveredOfBooked)} = ${num(c0.outboundDelivered)} delivered → × ${pct1(c0.outboundOpps / Math.max(1e-9, c0.outboundDelivered))} = ${num(c0.outboundOpps)} opps → × ${pct1(c0.winRate)} = ${num(obWins)} won → ${fmtM(obWins * c0.avgWonValue)}`
    : '';
  const chainDeal = c0
    ? `${num(c0.opps)} opps × ${fmtM(inputs.avgCreatedOppValue)} = ${fmtM(c0.pipelineCreated)} pipeline → × ${pct1(c0.winRate)} = ${num(c0.wins)} won × ${fmtM(c0.avgWonValue)} = ${fmtM(c0.production)} new-logo ARR (${fmtM(c0.newArr)} lands in ${yearLabel(0)} after the ${String(Math.round(inputs.cycleDays))}-day cycle)`
    : '';
  // pipeline in hand (quick-261008-mi0): the live Salesforce slices behind the boxes, and the chain they drive
  const openPipe = baseline.openPipeline;
  const inHandSource = openPipe.computedAt
    ? `Salesforce, as of ${new Date(openPipe.computedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
    : 'Salesforce cache not populated yet — defaults to 0';
  const q4ByPositionText =
    Object.keys(openPipe.priorQ4ByPosition).length > 0
      ? ['Commit', 'Most Likely', 'Best Case', 'Pipeline', 'No position']
          .flatMap((p) => {
            const c = openPipe.priorQ4ByPosition[p];
            return c ? [`${p} ${fmtM(c.amount)} (${String(c.oppCount)})`] : [];
          })
          .join(' · ')
      : 'none open';
  const ih = pipelineInHand(inputs);
  const chainInHand = c0
    ? `${fmtM(ih.open)} open ${yearLabel(0)} + ${pct1(inputs.priorQ4RollForwardPct)} × ${fmtM(inputs.pipelineInHandPriorQ4)} Q4 ${yearLabel(-1)} best case/pipeline = ${fmtM(ih.total)} in hand → ÷ ${fmtM(inputs.avgCreatedOppValue)} = ${num(req.pipelineInHandOpps)} opps → × ${pct1(c0.winRate)} = ${num(req.pipelineInHandOpps * c0.winRate)} won × ${fmtM(c0.avgWonValue)} = ${fmtM(req.pipelineInHandExpectedArr)} expected new-logo ARR · the ${yearLabel(0)} target needs ${fmtM(req.requiredPipeline)} of pipeline, so ${fmtM(req.pipelineToGenerate)} is left to generate`
    : '';
  const chainGrowth = result.years
    .map((y, i) => `${yearLabel(i)} ${fmtM(y.newArr)}${y.newArrYoY == null ? '' : ` (${y.newArrYoY >= 0 ? '+' : ''}${pct1(y.newArrYoY)})`}`)
    .join(' → ');
  const activePreset = preset === 'custom' ? undefined : MODEL3Y_PRESETS.find((p) => p.id === preset);
  const presetLabel = preset === 'custom' ? 'Custom (edited)' : (activePreset?.label ?? '');
  const presetNote = activePreset?.describe ? activePreset.describe(actuals, ctx) : '';

  return (
    <div className="space-y-5" data-testid="three-year-model">
      {/* Presets */}
      <div className="rounded-[14px] border border-ink-100 bg-white p-4 shadow-[0_1px_2px_rgba(16,28,45,0.05)]">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="max-w-[80ch] text-xs text-ink-700">
            Inputs on top, results underneath. Every rate starts at today&rsquo;s actual with its benchmark beside it; change any
            box and everything below recalculates. The page opens on <b className="font-semibold text-ink-900">Plan growth</b>{' '}
            (the plan shape, win rate solved to {String(Math.round(PLAN_ARR_GROWTH * 100))}% compound ARR growth);{' '}
            <b className="font-semibold text-ink-900">Likely</b> is the last two months&rsquo; run rate, flat;{' '}
            <b className="font-semibold text-ink-900">Actuals (no growth)</b> is the flat case on the full window.
          </p>
          <span className="text-[11px] text-ink-500">
            Preset: <b className="font-semibold text-ink-700">{presetLabel}</b>
            {presetNote && (
              <span className="block max-w-[60ch] text-[10.5px] leading-tight text-ink-500" data-testid="preset-note">
                {presetNote}
              </span>
            )}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Scenario presets">
          {MODEL3Y_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              title={presetDescription(p)}
              onClick={() => {
                applyPreset(p.id);
              }}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                preset === p.id
                  ? 'border-adeptia-cyan bg-adeptia-cyan/15 text-ink-900'
                  : p.id === 'actuals'
                    ? 'border-ink-900 bg-ink-900 text-white hover:opacity-90'
                    : 'border-ink-200 bg-white text-ink-700 hover:border-adeptia-cyan'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* 1 · New-logo inputs */}
      <Section
        n="1 · New-logo inputs"
        title="The new-logo funnel"
        feeds="Everything in this section feeds the new-logo line only: inbound leads and outbound conversations → meetings → New Business opportunities → pipeline → new-logo ARR. None of it changes expansion."
        tone="cyan"
      >
        <Group title="Capacity — the volume the team generates (BDRs and AEs both dial)" chain={chainCapacity}>
          <Field id="bdrs" label="BDRs" kind="int" value={inputs.bdrs} actual={actuals.bdrs} source={`headcount; BDRs book meetings, they do not close (${wb})`} onChange={setField} />
          <Field id="bdrRampMonths" label="New-BDR ramp (months)" kind="int" value={inputs.bdrRampMonths} actual={actuals.bdrRampMonths} source="applies to BDRs added above today's; assumption" onChange={setField} />
          <Field id="conversationsPerBdrMonth" label="Conversations per BDR per month" kind="num" value={inputs.conversationsPerBdrMonth} actual={actuals.conversationsPerBdrMonth} source={`BDR-owned M3 dispositions that reached a human (${wb})`} onChange={setField} />
          <Field id="sellers" label="Quota-carrying sellers (AEs)" kind="int" value={inputs.sellers} actual={actuals.sellers} source={`headcount; AEs work the delivered meetings and close (${wb})`} onChange={setField} />
          <Field id="rampMonths" label="New-AE ramp (months)" kind="int" value={inputs.rampMonths} actual={actuals.rampMonths} source="applies to AEs added above today's" onChange={setField} />
          <Field id="conversationsPerSellerMonth" label="Conversations per AE per month (their own dials)" kind="num" value={inputs.conversationsPerSellerMonth} actual={actuals.conversationsPerSellerMonth} source={`AE and sales-manager M3 dispositions only; BDR calls are counted above (${wb})`} onChange={setField} />
          <Field id="inboundLeadsPerMonth" label="Inbound leads / month" kind="num" value={inputs.inboundLeadsPerMonth} actual={actuals.inboundLeadsPerMonth} source={`non-DQ inbound leads, ${wb}`} onChange={setField} />
        </Group>
        <Group title="Inbound funnel — leads → booked → delivered → opportunities → won" chain={chainInbound}>
          <Field id="leadToBooked" label="Lead → booked meeting" kind="pct" value={inputs.leadToBooked} actual={actuals.leadToBooked} source={`M1, matured non-DQ leads (${wb})`} onChange={setField} />
          <Field id="inboundDeliveredOfBooked" label="Booked → delivered" kind="pct" value={inputs.inboundDeliveredOfBooked} actual={actuals.inboundDeliveredOfBooked} source={`M1 delivered of booked (${wb})`} onChange={setField} />
          <Field id="inboundMtgToOpp" label="Delivered meeting → opportunity" kind="pct" value={inputs.inboundMtgToOpp} actual={actuals.inboundMtgToOpp} source={`M2 ${pct1(fb.inboundMtgToOpp)} ${traceNote}`} onChange={setField} />
        </Group>
        <Group title="Outbound funnel — conversations → booked → delivered → opportunities → won" chain={chainOutbound}>
          <Field id="conversationsPerMeeting" label="Conversations per booked meeting (efficiency)" kind="num" value={inputs.conversationsPerMeeting} actual={actuals.conversationsPerMeeting} source={`how many conversations it takes to book one meeting; M3 (${wb}); benchmark 6–7`} onChange={setField} />
          <Field id="outboundDeliveredOfBooked" label="Booked → delivered" kind="pct" value={inputs.outboundDeliveredOfBooked} actual={actuals.outboundDeliveredOfBooked} source={`M4 delivered of come-due (${wb})`} onChange={setField} />
          <Field id="outboundMtgToOpp" label="Delivered meeting → opportunity" kind="pct" value={inputs.outboundMtgToOpp} actual={actuals.outboundMtgToOpp} source={`M4 ${pct1(fb.outboundMtgToOpp)} ${traceNote}`} onChange={setField} />
        </Group>
        <Group title="After the meeting — AE capacity" chain={chainAeCapacity}>
          <Field id="meetingsPerAeMonth" label="Delivered meetings an AE can work / month" kind="num" value={inputs.meetingsPerAeMonth} actual={actuals.meetingsPerAeMonth} source="assumption, not measured; today's AEs run about 5 a month each. Meetings above the cap make no opportunity" onChange={setField} />
        </Group>
        <Group title="Deal economics — opportunities → pipeline → won → new-logo ARR (New Business only)" chain={chainDeal}>
          <Field id="avgCreatedOppValue" label="Average created new-logo opportunity" kind="money" value={inputs.avgCreatedOppValue} actual={actuals.avgCreatedOppValue} source={`New Business opps created (${wbDeal}); all-Product ${fmtM(baseline.avgCreatedOppValue)}`} onChange={setField} />
          <Field id="winRate" label="New-logo win rate" kind="pct" value={inputs.winRate} actual={actuals.winRate} source={`M5 New Business, Closed Won ÷ (Won + Lost) (${wbDeal}); all-Product MAIN-24 ${pct1(baseline.winRate)}`} onChange={setField} />
          <Field id="avgWonValue" label="Average won new-logo deal" kind="money" value={inputs.avgWonValue} actual={actuals.avgWonValue} source={`M7 New Business (${wbDeal}); all-Product MAIN-12 ${fmtM(baseline.avgWonValue)}`} onChange={setField} />
          <Field id="cycleDays" label="Sales cycle (days, opp → close)" kind="int" value={inputs.cycleDays} actual={actuals.cycleDays} source={`New Business won, average created → close, ${wbDeal} (9 wins, the 684-day Pacific Life record left out; the all-Product median read 64). Sets how much of a year's pipeline lands in the next year`} onChange={setField} />
        </Group>
        <Group title={`Growth per year — what makes ${yearLabel(1)} and ${yearLabel(2)} differ from ${yearLabel(0)}`} chain={chainGrowth}>
          <Field id="bdrsAddedPerYear" label="BDRs added each later year" kind="int" value={inputs.bdrsAddedPerYear} actual={actuals.bdrsAddedPerYear} source={`hired at the start of ${yearLabel(1)} and ${yearLabel(2)}; each cohort ramps in its year`} onChange={setField} />
          <Field id="sellersAddedPerYear" label="AEs added each later year" kind="int" value={inputs.sellersAddedPerYear} actual={actuals.sellersAddedPerYear} source={`hired at the start of ${yearLabel(1)} and ${yearLabel(2)}; each cohort ramps in its year`} onChange={setField} />
          <Field id="inboundGrowthPct" label="Inbound lead growth / yr" kind="pct" value={inputs.inboundGrowthPct} actual={actuals.inboundGrowthPct} source="measured: +7% H1-26 vs H2-25 but −8% YTD vs 2025 → no trend; compounding" onChange={setField} />
          <Field id="conversionImprovementPct" label="Conversion improvement / yr" kind="pct" value={inputs.conversionImprovementPct} actual={actuals.conversionImprovementPct} source="relative, compounding: lead→booked, meeting→opp, conversations per meeting" onChange={setField} />
          <Field id="annualDrift" label="Win rate & deal size change / yr" kind="pct" value={inputs.annualDrift} actual={actuals.annualDrift} source="relative, compounding; 0 = hold flat" onChange={setField} />
        </Group>
      </Section>

      {/* 2 · Pipeline in hand (quick-261008-mi0) */}
      <Section
        n="2 · Pipeline in hand"
        title={`New-business pipeline that already exists for ${yearLabel(0)}`}
        feeds={`Feeds only the "what is needed to generate" math in the results: this pipeline is subtracted from what the ${yearLabel(0)} target needs before the funnel is asked to create the rest, and its expected yield counts toward the target. Live from Salesforce; edit a box to test a different assumption.`}
        tone="cyan"
      >
        <Group title={`Open New Business pipeline counted for ${yearLabel(0)}`} chain={chainInHand}>
          <Field
            id="pipelineInHandOpen"
            label={`Open pipeline close-dated in ${yearLabel(0)}`}
            kind="money"
            value={inputs.pipelineInHandOpen}
            actual={actuals.pipelineInHandOpen}
            source={`${String(openPipe.planYear.oppCount)} open New Business Product opps with a ${yearLabel(0)} close date, any forecast position (${inHandSource})`}
            step={10_000}
            onChange={setField}
          />
          <Field
            id="pipelineInHandPriorQ4"
            label={`Q4 ${yearLabel(-1)} open pipeline at Best Case + Pipeline`}
            kind="money"
            value={inputs.pipelineInHandPriorQ4}
            actual={actuals.pipelineInHandPriorQ4}
            source={`Q4 ${yearLabel(-1)} open New Business by position: ${q4ByPositionText}. Only ${PRIOR_Q4_ROLL_FORWARD_POSITIONS.join(' + ')} roll forward; Commit and Most Likely stay in ${yearLabel(-1)}`}
            step={10_000}
            onChange={setField}
          />
          <Field
            id="priorQ4RollForwardPct"
            label={`Share of Q4 ${yearLabel(-1)} best case + pipeline that rolls into ${yearLabel(0)}`}
            kind="pct"
            value={inputs.priorQ4RollForwardPct}
            actual={actuals.priorQ4RollForwardPct}
            source={`John 2026-10-08: 75% of Q4 best case and pipeline rolls into ${yearLabel(0)} — most of it will not close in Q4`}
            step={5}
            onChange={setField}
          />
        </Group>
      </Section>

      {/* 3 · Expansion inputs */}
      <Section
        n="3 · Expansion inputs"
        title="Expansion from current customers, and the base it sits on"
        feeds="Feeds only the expansion line and the retained base of the total ARR forecast. Nothing here changes the new-logo funnel. Expansion is expected to come from current customers at an improvement on the historical rate — enter the dollars you expect per year."
        tone="green"
      >
        <Group title="Existing base (Rillet)">
          <Field id="startingArr" label={`Opening ARR, start of ${yearLabel(0)}`} kind="money" value={inputs.startingArr} actual={actuals.startingArr} source={`${fb.startingArrBasis}; Rillet active ARR was ${fmtM(fb.rilletActiveArrAug2026)} in Aug 2026`} step={100_000} onChange={setField} />
          <Field
            id="expansionArrPerYear"
            label="Expansion ARR from current customers / yr"
            kind="money"
            value={inputs.expansionArrPerYear}
            actual={actuals.expansionArrPerYear}
            source={`historical Rillet ${fmtM(fb.expansionHistoricalArr)} = ${pct1(fb.expansionHistoricalPct)} of the base (5.9 / 21.3 / 14.7% over three years); set above it to model an improvement`}
            step={50_000}
            onChange={setField}
          />
          <Field id="churnPct" label="Churn + contraction, % of opening ARR / yr" kind="pct" value={inputs.churnPct} actual={actuals.churnPct} source="Rillet, trailing year" onChange={setField} />
        </Group>
        <p className="text-[10.5px] leading-tight text-ink-500">
          Year 1 expansion = the dollars entered ({fmtM(inputs.expansionArrPerYear)}, {pct1(result.expansionPct)} of starting ARR,{' '}
          {expansionVsHistorical(inputs.expansionArrPerYear, fb.expansionHistoricalArr)} the historical {fmtM(fb.expansionHistoricalArr)});
          later years scale with the book. Churn applies to each year&rsquo;s opening balance. Existing-customer opportunities in
          Salesforce are not in the funnel above — they are covered here.
        </p>
      </Section>

      {/* 3 · Results */}
      <Section
        n="4 · Results"
        title="Total ARR forecast — new logo vs expansion — and the pipeline the target needs"
        feeds="Section 1 drives the new-logo line and the pipeline math; section 2 drives the expansion line and the retained base. The target box at the bottom changes only the required-pipeline comparison."
        tone="ink"
      >
        {/* Headline tiles */}
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-ink-100 bg-ink-100 sm:grid-cols-3 lg:grid-cols-4">
          <Out label="ARR in 3 years" value={fmtM(result.endingArr)} note={`from ${fmtM(inputs.startingArr)} opening ${yearLabel(0)}`} />
          <Out label="Compound growth" value={pct1(result.cagr)} note="per year" />
          <Out
            label={`New-logo ARR ${yearLabel(0)} → ${yearLabel(2)}`}
            value={`${fmtM(result.years[0]?.newArr ?? 0)} → ${fmtM(result.years[2]?.newArr ?? 0)}`}
            note={result.years.map((y, i) => `${yearLabel(i)} ${y.newArrYoY == null ? '' : (y.newArrYoY >= 0 ? '+' : '') + pct1(y.newArrYoY)}`).join(' · ')}
          />
          <Out label={`Expansion ${yearLabel(0)}`} value={fmtM(y1?.expansion ?? 0)} note={`${expansionVsHistorical(inputs.expansionArrPerYear, fb.expansionHistoricalArr)} historical`} />
          <Out label={`Pipeline needed ${yearLabel(0)}`} value={fmtM(req.requiredPipeline)} note={`${req.coverageMultiple.toFixed(1)}× the ${fmtM(req.target)} target · ${pct1(req.attainment)} attained with pipeline in hand`} tone={verdictTone} />
          <Out
            label={`Pipeline in hand ${yearLabel(0)}`}
            value={fmtM(req.pipelineInHand)}
            note={`${fmtM(req.pipelineInHandOpen)} open ${yearLabel(0)} + ${fmtM(req.pipelineInHandPriorQ4Rolled)} rolled from Q4 ${yearLabel(-1)} (${pct1(inputs.priorQ4RollForwardPct)} of best case + pipeline) → ${fmtM(req.pipelineInHandExpectedArr)} expected ARR`}
          />
          <Out
            label={`Pipeline to generate ${yearLabel(0)}`}
            value={fmtM(req.pipelineToGenerate)}
            note={`needed ${fmtM(req.requiredPipeline)} − in hand ${fmtM(req.pipelineInHand)}; the funnel creates ${fmtM(steady.pipelineCreated)} (${num(steady.opps)} opps at ${fmtM(inputs.avgCreatedOppValue)})`}
            tone={req.pipelineToGenerate <= steady.pipelineCreated * 1.02 ? 'good' : req.pipelineToGenerate <= steady.pipelineCreated * 1.25 ? 'warn' : 'bad'}
          />
          <Out label={`Pipeline created ${yearLabel(0)}`} value={fmtM(steady.pipelineCreated)} note={`${num(steady.opps)} new-logo opps at ${fmtM(inputs.avgCreatedOppValue)}; ${yearLabel(2)} ${fmtM(result.years[2]?.pipelineCreated ?? 0)}`} />
        </div>

        {/* Chart */}
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-500">Projected ARR build</div>
          <div className="h-[280px] w-full" data-testid="three-year-chart">
            <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} notMerge lazyUpdate={false} />
          </div>
        </div>

        {/* Total ARR table */}
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-500">Total ARR forecast by year</div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs" data-testid="total-arr-table">
              <thead>
                <tr className="border-b border-ink-100 text-[10px] uppercase tracking-wider text-ink-500">
                  <th className="py-1 pr-2 text-left font-semibold">Year</th>
                  <th className="px-2 py-1 text-right font-semibold">Opening ARR</th>
                  <th className="px-2 py-1 text-right font-semibold">+ New logo</th>
                  <th className="px-2 py-1 text-right font-semibold">New-logo YoY</th>
                  <th className="px-2 py-1 text-right font-semibold">+ Expansion (current customers)</th>
                  <th className="px-2 py-1 text-right font-semibold">&minus; Churn</th>
                  <th className="px-2 py-1 text-right font-semibold">Ending ARR</th>
                  <th className="px-2 py-1 text-right font-semibold">Growth</th>
                </tr>
              </thead>
              <tbody className="text-ink-700">
                {result.years.map((y, i) => (
                  <tr key={y.index} className="border-b border-ink-100/60">
                    <td className="py-1 pr-2 text-left font-medium">{yearLabel(i)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtM(y.openingArr)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-[var(--color-adeptia-cyan)]">{fmtM(y.newArr)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${y.newArrYoY != null && y.newArrYoY < 0 ? 'text-bad-text' : 'text-ink-500'}`}>
                      {y.newArrYoY == null ? '—' : `${y.newArrYoY >= 0 ? '+' : ''}${pct1(y.newArrYoY)}`}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-good-text">{fmtM(y.expansion)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-bad-text">{fmtM(y.churn)}</td>
                    <td className="px-2 py-1 text-right font-semibold tabular-nums text-ink-900">{fmtM(y.endingArr)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{pct1(y.growthPct)}</td>
                  </tr>
                ))}
                <tr className="font-semibold text-ink-900">
                  <td className="py-1 pr-2 text-left">Three years</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtM(inputs.startingArr)}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-[var(--color-adeptia-cyan)]">
                    {fmtM(result.totals.newLogo)} <span className="font-normal text-ink-500">({pct1(result.totals.newLogoShareOfGross)})</span>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-ink-500">
                    {(() => {
                      const a = result.years[0]?.newArr ?? 0;
                      const b = result.years[2]?.newArr ?? 0;
                      return a > 0 ? `${pct1(Math.pow(b / a, 1 / 2) - 1)} / yr` : '—';
                    })()}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-good-text">
                    {fmtM(result.totals.expansion)} <span className="font-normal text-ink-500">({pct1(1 - result.totals.newLogoShareOfGross)})</span>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-bad-text">{fmtM(result.totals.churn)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtM(result.endingArr)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{pct1(result.cagr)} / yr</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-1.5 text-[10.5px] leading-tight text-ink-500">
            New-logo YoY for {yearLabel(0)} is against today&rsquo;s run-rate. New-logo ARR lags creation by the sales cycle (
            {String(Math.round(inputs.cycleDays))} days), so part of each year&rsquo;s pipeline closes the following year; the growth
            levers in section 1 (hires, inbound growth, conversion improvement, win-rate and deal-size change) are what move
            the years apart. NRR {pct1(result.nrr)}.
          </p>
        </div>

        {/* New-logo funnel by year */}
        <div>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-500">New-logo funnel by year</div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs" data-testid="funnel-by-year">
              <thead>
                <tr className="border-b border-ink-100 text-[10px] uppercase tracking-wider text-ink-500">
                  <th className="py-1 pr-2 text-left font-semibold">Year</th>
                  <th className="px-2 py-1 text-right font-semibold">BDRs</th>
                  <th className="px-2 py-1 text-right font-semibold">AEs</th>
                  <th className="px-2 py-1 text-right font-semibold">Inbound leads</th>
                  <th className="px-2 py-1 text-right font-semibold">Conversations</th>
                  <th className="px-2 py-1 text-right font-semibold">Meetings delivered</th>
                  <th className="px-2 py-1 text-right font-semibold">AE load</th>
                  <th className="px-2 py-1 text-right font-semibold">Opps</th>
                  <th className="px-2 py-1 text-right font-semibold">Pipeline</th>
                  <th className="px-2 py-1 text-right font-semibold">Win rate</th>
                  <th className="px-2 py-1 text-right font-semibold">New logos</th>
                  <th className="px-2 py-1 text-right font-semibold">New-logo ARR</th>
                  <th className="px-2 py-1 text-right font-semibold">YoY</th>
                </tr>
              </thead>
              <tbody className="text-ink-700">
                {result.years.map((y, i) => (
                  <tr key={y.index} className="border-b border-ink-100/60">
                    <td className="py-1 pr-2 text-left font-medium">{yearLabel(i)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.effectiveBdrs)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.effectiveSellers)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.inboundLeads)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.conversations)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.inboundDelivered + y.outboundDelivered)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${y.aeLoad > 1 ? 'font-semibold text-bad-text' : y.aeLoad > 0.85 ? 'text-warn-text' : ''}`}>
                      {pct1(y.aeLoad)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.opps)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtM(y.pipelineCreated)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{pct1(y.winRate)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{num(y.wins)}</td>
                    <td className="px-2 py-1 text-right font-semibold tabular-nums text-ink-900">{fmtM(y.newArr)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${y.newArrYoY != null && y.newArrYoY < 0 ? 'text-bad-text' : 'text-ink-500'}`}>
                      {y.newArrYoY == null ? '—' : `${y.newArrYoY >= 0 ? '+' : ''}${pct1(y.newArrYoY)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Required pipeline — the question */}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
            How much pipe does the new-logo target need, and can this funnel create it?
          </div>
          <div className="mt-2 max-w-4xl">
            <Field
              id="targetNewArrPerYear"
              label="Input · target new-logo ARR per year"
              kind="money"
              value={inputs.targetNewArrPerYear}
              actual={actuals.targetNewArrPerYear}
              source={`default = ${PLAN2027_NEW_LOGO_TARGET.basis}, ${fmtM(PLAN2027_NEW_LOGO_TARGET.amount)}; changes only this comparison`}
              step={100_000}
              onChange={setField}
            />
            <div className={`mt-3 rounded-[10px] border p-3 text-[12.5px] leading-snug ${verdictCls}`} data-testid="three-year-verdict">
              <b className="font-bold">
                {req.verdict === 'on-target'
                  ? 'On target.'
                  : req.verdict === 'gap-small'
                    ? 'Close, but short.'
                    : `Not with this funnel in ${yearLabel(0)}.`}
              </b>{' '}
              In {yearLabel(0)} the new-logo funnel produces <b className="font-semibold">{fmtM(steady.production)}</b> of new-logo ARR, and
              the pipeline already in hand is expected to add {fmtM(req.pipelineInHandExpectedArr)}, against a target of{' '}
              <b className="font-semibold">{fmtM(req.target)}</b> ({pct1(req.attainment)}); with the growth levers the funnel reaches{' '}
              {fmtM(result.years[2]?.production ?? 0)} by {yearLabel(2)}. The {yearLabel(0)} target needs{' '}
              <b className="font-semibold">{fmtM(req.requiredPipeline)}</b> of pipeline ({req.coverageMultiple.toFixed(1)}&times; coverage).{' '}
              <b className="font-semibold">{fmtM(req.pipelineInHand)}</b> is in hand ({fmtM(req.pipelineInHandOpen)} open {yearLabel(0)} +{' '}
              {pct1(inputs.priorQ4RollForwardPct)} of Q4 {yearLabel(-1)} best case and pipeline), leaving{' '}
              <b className="font-semibold">{fmtM(req.pipelineToGenerate)}</b> to generate — the modeled funnel creates{' '}
              {fmtM(steady.pipelineCreated)}.
            </div>
            <div className="mt-3 rounded-[10px] border border-ink-100 bg-ink-50 p-3 text-[12px] leading-snug text-ink-700" data-testid="coverage-why">
              <b className="font-semibold text-ink-900">Why {req.coverageMultiple.toFixed(1)}&times;.</b> One new-logo opp is{' '}
              {fmtD(inputs.avgCreatedOppValue)} of pipeline. It closes {pct1(steady.winRate)} of the time at {fmtD(steady.avgWonValue)}, so
              it is worth {fmtD(req.expectedArrPerOpp)} of expected ARR. {fmtD(inputs.avgCreatedOppValue)} &divide; {fmtD(req.expectedArrPerOpp)} ={' '}
              {req.coverageMultiple.toFixed(1)}&times; pipeline per dollar of ARR. Because the created value and the won value are about
              the same, coverage is roughly 1 &divide; win rate: raise the win rate and the multiple falls.
            </div>
          </div>

          {/* The target walk (quick-261030-ka4) */}
          <div className="mt-4" data-testid="target-walk">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
              The {fmtM(walk.target)} target walked back through today&rsquo;s funnel
            </div>
            <p className="mt-1 text-[12px] leading-snug text-ink-700" data-testid="walk-legend">
              <b className="font-semibold text-ink-900">Run rate</b> = the last six months ({fb.funnelWindow.replace(/ \(last 6 months\)$/, '')}; deal
              size and win rate {fb.dealWindow}) run for a year at today&rsquo;s team. It is built from actuals only, so editing an
              input never moves it. <b className="font-semibold text-ink-900">Needed</b> = the target worked backward with every
              rate held at today&rsquo;s value: target &divide; average deal = wins &divide; win rate = opportunities, less the{' '}
              {num(walk.rows.find((r) => r.key === 'inHand')?.needed ?? 0)} already open, split{' '}
              {pct1(walk.inboundShare)} inbound / {pct1(1 - walk.inboundShare)} outbound like today, then back through
              meeting&rarr;opp, show rate, lead&rarr;booked and conversations per meeting to the activity and headcount. The grey
              line under each number shows the arithmetic. <b className="font-semibold text-ink-900">Gap</b> = needed &minus; run
              rate. <b className="font-semibold text-ink-900">Improved</b> = the scenario above ({presetLabel}), first plan year.
            </p>
            <div className="mt-2 grid grid-cols-1 gap-4 xl:grid-cols-4">
              <div className="overflow-x-auto xl:col-span-3">
                <table className="w-full border-collapse text-xs" data-testid="required-chain">
                  <thead>
                    <tr className="border-b border-ink-100 text-[10px] uppercase tracking-wider text-ink-500">
                      <th className="py-1 pr-2 text-left font-semibold">Per year, {yearLabel(0)}</th>
                      <th className="px-2 py-1 text-right font-semibold">Run rate (actuals)</th>
                      <th className="px-2 py-1 text-right font-semibold">Needed at today&rsquo;s rates</th>
                      <th className="px-2 py-1 text-right font-semibold">Gap</th>
                      <th className="px-2 py-1 text-right font-semibold">Improved funnel</th>
                      <th className="py-1 pl-3 text-left font-semibold">What the improved funnel assumes</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink-700">
                    {walk.rows.map((r, i) => {
                      const prev = walk.rows[i - 1];
                      const gap = r.needed - r.runRate;
                      const short = !r.rate && gap > 1e-6;
                      const covered = r.rate
                        ? r.lowerIsBetter
                          ? r.improved <= r.needed + 1e-9
                          : r.improved >= r.needed - 1e-9
                        : r.improved >= r.needed * 0.98;
                      const emphasis = r.key === 'newArr' || r.key === 'opps' || r.key === 'conversations' || r.key === 'leads';
                      return (
                        <Fragment key={r.key}>
                          {prev?.group !== r.group ? (
                            <tr>
                              <td colSpan={6} className="pb-0.5 pt-2.5 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                                {r.group}
                              </td>
                            </tr>
                          ) : null}
                          <tr className={`border-b border-ink-100/60 align-top ${emphasis ? 'font-semibold text-ink-900' : ''} ${r.rate ? 'text-ink-500' : ''}`} data-testid={`walk-${r.key}`}>
                            <td className="py-1 pr-2 text-left">{r.label}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{fmtWalk(r.runRate, r.unit)}</td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {fmtWalk(r.needed, r.unit)}
                              <div className="text-[10px] font-normal leading-tight text-ink-500">{r.derivation}</div>
                            </td>
                            <td className={`px-2 py-1 text-right tabular-nums ${short ? 'text-bad-text' : 'text-good-text'}`}>
                              {r.rate ? <span className="text-ink-300">held</span> : `${gap > 1e-6 ? '+' : '−'}${fmtWalk(Math.abs(gap), r.unit)}`}
                            </td>
                            <td className={`px-2 py-1 text-right tabular-nums ${covered ? 'text-good-text' : 'text-warn-text'}`}>{fmtWalk(r.improved, r.unit)}</td>
                            <td className="py-1 pl-3 text-left text-[11px] font-normal leading-tight text-ink-700">
                              {r.assumptions.length ? r.assumptions.join(' · ') : <span className="text-ink-300">as today</span>}
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-1.5 text-[10.5px] leading-tight text-ink-500">
                  Gap: red = more needed than the run rate makes. Improved: green = at or past what the target needs. Meeting&rarr;opp
                  rates carry the trace scale (every New Business opp is meeting-sourced). Headcount is ramp-adjusted for the first
                  plan year.
                </p>
              </div>
              <aside className="rounded-[10px] border border-ink-100 bg-ink-50 p-3 text-[12px] leading-snug text-ink-700" data-testid="walk-assumptions">
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Assumptions in the improved funnel</div>
                <p className="mt-1">
                  <b className="font-semibold text-ink-900">{presetLabel}.</b> {activePreset ? presetDescription(activePreset) : 'Edited by hand.'}
                </p>
                {walk.assumptions.length === 0 ? (
                  <p className="mt-2 text-ink-500">No change from the last six months, so the improved column equals the run rate. Pick a preset or edit an input above.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {walk.assumptions.map((x) => (
                      <li key={x.input}>
                        {x.text}
                        <div className="text-[10.5px] text-ink-500">{x.outsideFirstYear ? 'not in this table' : `moves: ${x.rows.join(', ')}`}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-[10.5px] text-ink-500">
                  {fmtM(walk.rows.find((r) => r.key === 'newArr')?.improved ?? 0)} improved vs {fmtM(walk.target)} needed vs{' '}
                  {fmtM(walk.rows.find((r) => r.key === 'newArr')?.runRate ?? 0)} at the run rate.
                </p>
              </aside>
            </div>
          </div>
        </div>

        {/* What it would take */}
        <div data-testid="lever-options">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">What it would take to hit the {yearLabel(0)} target</div>
          {levers.onTarget ? (
            <p className="mt-2 text-sm font-semibold text-good-text">On target. No change needed.</p>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <LeverList title="On a single lever" items={levers.single} />
              <LeverList title="On multiple levers" items={levers.multi} />
            </div>
          )}
          <p className="mt-2 text-[10.5px] leading-tight text-ink-500">
            Doable = within one hiring class (+3 BDRs and +3 AEs), a 22% win rate, a 42% meeting&rarr;opp rate, +25% deal size under $150K,
            +50% inbound in a year, or no better than 7 conversations per meeting. Each line is solved against the {yearLabel(0)}{' '}
            funnel above.
          </p>
        </div>

        <p className="text-[10.5px] leading-tight text-ink-500">
          Sources: funnel-stage rates, inbound leads, conversation volume and New Business opportunities created are Salesforce
          measurements over {fb.funnelWindow} (pull {fb.pulledOn}); New Business win rate, deal sizes and the sales cycle (average
          created &rarr; close of won deals) are {fb.dealWindow}; pipeline in hand is live; the
          opening ARR is the {fb.startingArrBasis} (expansion and churn history from Rillet). Definitions: delivered
          meetings; win rate = Closed Won &divide; (Closed Won + Closed Lost). Nothing is saved — reload returns to actuals.
        </p>
      </Section>
    </div>
  );
}
